import * as queryString from 'query-string';

import getCookie from './cookies';
import isBrowser from '../isBrowser';
import { isAbsoluteURL, isLucidAppAllowedPathnames } from '../validators/regex';

type SimpleURL = {
  hash?: string;
  host?: string;
  hostname?: string;
  href?: string;
  origin?: string;
  password?: string;
  pathname?: string;
  port?: string;
  protocol?: string;
  search?: string;
};

const withBeaconURLParams = (url?: string, willAddBeaconParams = false) => {
  if (!url) return;
  /** Allowed pathnames
   * /users/login
   * /users/register
   * /users/registerLevel
   * /users/payLevel
   * /users/googleFederatedLogin
   * /documents
   * /lucidchart/editNewOrRegister
   * /lucidscale/editNewOrRegister
   * /lucidspark/editNewOrRegister
   * /pricing/lucidchart
   * /pricing/lucidscale
   * /pricing/lucidspark
   */
  let newUrlObj = {} as SimpleURL;
  let params = {};
  let sessionDataRaw: string | undefined = '{"id": "undefined", "lastUpdatedDate": "undefined"}';
  let anonymousId: string | undefined = 'undefined';

  // create url object if an absolute url is provided
  if (url.match(isAbsoluteURL)) {
    newUrlObj = new URL(url);
  }

  // create url object if a relative url is provided with current origin
  if (!url.match(isAbsoluteURL)) {
    if (isBrowser()) {
      const { origin } = window.location;
      newUrlObj = new URL(url, origin);
    }
  }

  // if url pathname doesn't match allowedPathnames regex return current url
  // if willAddBeaconParams is true, allow beacon cookie sync params to be appended
  if (
    !newUrlObj?.pathname?.match(isLucidAppAllowedPathnames) &&
    !newUrlObj?.origin?.match(/lucid\.app/gim) &&
    !willAddBeaconParams
  ) {
    return url;
  }

  // check if url has current query params, and include them in params object
  if (newUrlObj && newUrlObj.search) {
    const currentSearch = queryString.parse(newUrlObj.search);
    params = currentSearch;
  }

  // grab both cookies to add to url query params
  if (isBrowser()) {
    sessionDataRaw = getCookie('lt-session-data');
    anonymousId = getCookie('lt-anonymous-id');
  }

  // set sessionData params if it exists
  if (sessionDataRaw) {
    try {
      const sessionData: { [key: string]: string } = JSON.parse(sessionDataRaw);
      if (sessionData) {
        const sessionId = sessionData['id'];
        const sessionDate = sessionData['lastUpdatedDate'];
        if (sessionId) {
          params = {
            ...params,
            sessionId,
          };
        }

        if (sessionDate) {
          params = {
            ...params,
            sessionDate,
          };
        }
      }
    } catch (e) {
      console.info('Cookie Sync Params Error:', e);
    }
  }

  // set anonId param if it exists
  if (anonymousId) {
    params = { ...params, anonId: anonymousId };
  }

  const { origin, pathname } = newUrlObj;
  // stringify url params
  const queryStringParams = queryString.stringify(params);

  // return newly constructed url
  return `${origin}${pathname ? pathname : '/'}${queryStringParams ? `?${queryStringParams}` : ''}`;
};

export default withBeaconURLParams;
